<script>
import AutomationActionOdooCreateInput from './AutomationActionOdooCreateInput.vue';
import AutomationActionOdooUpdateInput from './AutomationActionOdooUpdateInput.vue';
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionUserTeamCollaboratorsInput from './AutomationActionUserTeamCollaboratorsInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import AutomationActionCustomAttributesInput from './AutomationActionCustomAttributesInput.vue';
import AutomationActionEventCustomAttributeInput from './AutomationActionEventCustomAttributeInput.vue';
import AutomationActionEmailInput from './AutomationActionEmailInput.vue';
import RichTextEditor from 'dashboard/routes/dashboard/settings/components/RichTextEditor.vue';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AutomationActionColorInput from './AutomationActionColorInput.vue';

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionUserTeamCollaboratorsInput,
    AutomationActionFileInput,
    AutomationActionEmailInput,
    RichTextEditor,
    AutomationActionOdooCreateInput,
    AutomationActionOdooUpdateInput,
    AutomationActionCustomAttributesInput,
    AutomationActionEventCustomAttributeInput,
    NextButton,
    SingleSelect,
    MultiSelect,
    NextInput,
    AutomationActionColorInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
    dropdownMaxHeight: {
      type: String,
      default: 'max-h-80',
    },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      return this.actionTypes.find(action => action.key === this.action_name)
        ?.inputType;
    },
    actionNameAsSelectModel: {
      get() {
        if (!this.action_name) return null;
        const found = this.actionTypes.find(a => a.key === this.action_name);
        return found ? { id: found.key, name: found.label } : null;
      },
      set(value) {
        this.action_name = value?.id || value;
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes.map(a => ({ id: a.key, name: a.label }));
    },
    isVerticalLayout() {
      return [
        'team_message',
        'textarea',
        'color',
        'user_team_collaborators',
        'event_custom_attribute',
      ].includes(this.inputType);
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    automationVariables() {
      return MESSAGE_VARIABLES.map(v => ({ name: v.key, label: v.label }));
    },
    castOdooVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    castCollaboratorsVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0] || { user_ids: [], team_ids: [] };
        }
        return this.action_params || { user_ids: [], team_ids: [] };
      },
      set(value) {
        this.action_params = value;
      },
    },
    castColorVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0] || '';
        }
        return this.action_params || '';
      },
      set(value) {
        this.action_params = [value];
      },
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },
  },
};
</script>

<template>
  <li class="list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div class="flex items-end gap-2">
        <SingleSelect
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          disable-deselect
          class="flex-shrink-0"
          @update:model-value="onActionNameChange"
        />
        <template v-if="showActionInput && !isVerticalLayout">
          <SingleSelect
            v-if="inputType === 'search_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <MultiSelect
            v-else-if="inputType === 'multi_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <AutomationActionCustomAttributesInput
            v-else-if="inputType === 'custom_attributes_multi_select'"
            v-model="action_params"
          />
          <span
            v-else-if="inputType === 'email'"
            class="text-sm font-semibold text-n-slate-11 flex-shrink-0 pb-1"
          >
            {{ $t('AUTOMATION.ACTION.EMAIL_TO_LABEL') }}
          </span>
          <AutomationActionEmailInput
            v-if="inputType === 'email'"
            v-model="action_params"
          />
          <NextInput
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <AutomationActionFileInput
            v-else-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
        </template>
        <NextButton
          v-if="!isMacro"
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click="removeAction"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <AutomationActionUserTeamCollaboratorsInput
        v-if="inputType === 'user_team_collaborators'"
        v-model="castCollaboratorsVmodel"
        :placeholder="
          $t('AUTOMATION.ACTION.USER_TEAM_COLLABORATORS_PLACEHOLDER')
        "
      />
      <RichTextEditor
        v-if="inputType === 'textarea'"
        v-model="castMessageVmodel"
        show-toolbar
        :show-color-picker="false"
        output-format="text"
        variable-format="liquid"
        :variables="automationVariables"
        min-height="6rem"
        max-height="12rem"
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
      />
      <AutomationActionColorInput
        v-if="inputType === 'color'"
        v-model="castColorVmodel"
      />
      <AutomationActionEventCustomAttributeInput
        v-if="inputType === 'event_custom_attribute'"
        v-model="action_params"
      />
    </div>
    <AutomationActionOdooUpdateInput
      v-if="inputType === 'odoo_update'"
      v-model="castOdooVmodel"
    />
    <AutomationActionOdooCreateInput
      v-if="inputType === 'odoo_create'"
      v-model="castOdooVmodel"
    />
    <span v-if="errorMessage" class="filter-error">
      {{ errorMessage }}
    </span>
  </li>
</template>
